import React from "react";
import { StatusPill } from "@/ds";
import { fmtStamp } from "@/lib";
import type { OwnerRuleValueDelegation, OwnerTenantRuleParameters, RuleValueGrant, RuleParameterDefinition } from "@/api";
import { formatRuleMoney, ruleValueNumber } from "./rule-parameter-display";
import { useLang, useT } from "../i18n";

export function TenantRuleHistory({ values, delegation }: { values: OwnerTenantRuleParameters; delegation: OwnerRuleValueDelegation }) {
  const t = useT();
  const { lang } = useLang();
  const shown = (value: number | boolean, money = false) => typeof value === "boolean"
    ? t(value ? "businesses.rules.yes" : "businesses.rules.no") : money ? formatRuleMoney(value, lang) : String(value);
  const grantText = (grant: RuleValueGrant | undefined, unit: RuleParameterDefinition["unit"]) => {
    if (!grant?.editable) return t("businesses.rules.plane.platform");
    const plane = t("businesses.rules.plane.business");
    return grant.min === undefined ? plane
      : `${plane} · ${t("businesses.rules.delegation.range", { min: ruleValueNumber(grant.min, unit), max: ruleValueNumber(grant.max ?? 0, unit), step: ruleValueNumber(grant.step ?? 1, unit) })}`;
  };
  const entries = [
    ...values.history.map((entry, index) => {
      const previous = { ...values.defaults, ...values.history[index + 1]?.values };
      const current = { ...values.defaults, ...entry.values };
      const changed = values.parameters.filter((parameter) => current[parameter.key] !== previous[parameter.key]);
      return {
        id: entry.set_id, version: entry.version, kind: "values", stamp: entry.created_at,
        actor: entry.created_by_actor, actorId: entry.created_by_actor_id,
        active: values.source === "tenant_set" && entry.version === values.version,
        changes: changed.map((parameter) => `${t(`businesses.rules.param.${parameter.key}`, undefined, parameter.key)}: ${shown(previous[parameter.key], parameter.unit === "eur_cents")} → ${shown(current[parameter.key], parameter.unit === "eur_cents")}`),
      };
    }),
    ...delegation.history.map((entry, index) => {
      const previous = delegation.history[index + 1]?.keys ?? {};
      return {
        id: entry.id, version: entry.version, kind: "delegation", stamp: entry.created_at,
        actor: entry.created_by_actor, actorId: entry.created_by_actor_id,
        active: entry.version === delegation.version,
        changes: values.parameters.flatMap((parameter) => {
          const before = grantText(previous[parameter.key], parameter.unit);
          const after = grantText(entry.keys[parameter.key], parameter.unit);
          return before === after ? [] : [`${t(`businesses.rules.param.${parameter.key}`, undefined, parameter.key)}: ${before} → ${after}`];
        }),
      };
    }),
  ].sort((a, b) => b.stamp.localeCompare(a.stamp) || b.version - a.version || a.id.localeCompare(b.id));

  return <div className="stack-xs reqventos-rule-parameters__history">
    {entries.length === 0 && <p className="subtle">{t("businesses.rules.historyEmpty")}</p>}
    <ol className="reqventos-rule-history">
      {entries.map((entry) => <li key={`${entry.kind}-${entry.id}`} className="reqventos-rule-history__entry">
        <span className="reqventos-rule-history__version">
          {t(`businesses.rules.history.${entry.kind}`, { version: entry.version })}
          {entry.active && <StatusPill tone="success" label={t("businesses.rules.active")} />}
        </span>
        <span className="stack-xs subtle">
          <span>{fmtStamp(entry.stamp)}</span>
          <span title={entry.actor ? t(`role.${entry.actor.role}`, undefined, entry.actor.role) : undefined}>
            {entry.actor
              ? `${entry.actor.display_name} · ${t(`businesses.rules.plane.${entry.actor.plane}`)}`
              : t(entry.actorId ? "businesses.rules.actorUnavailable" : "businesses.rules.actorSystem")}
          </span>
        </span>
        <span>{entry.changes.join("; ") || t("businesses.rules.historyNoChange")}</span>
      </li>)}
    </ol>
  </div>;
}
